import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  TextField,
  IconButton,
  Alert,
} from '@mui/material'
import { useState, useEffect } from 'react'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import type { WhitelistEntry } from '@/types'
import { useInvoiceAnalyser } from '@/hooks/useInvoiceAnalyser'

interface SupplierWhitelistSectionProps {
  open?: boolean
}

export default function SupplierWhitelistSection({ open }: SupplierWhitelistSectionProps) {
  const [suppliers, setSuppliers] = useState<WhitelistEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const [newSupplier, setNewSupplier] = useState<WhitelistEntry>({
    id: '',
    names: [''],
    normalizedName: '',
    ustId: ''
  })

  const {
    getSupplierWhitelist,
    addSupplierToWhitelist,
    deleteSupplierFromWhitelist,
  } = useInvoiceAnalyser()

  // Load whitelist when dialog opens
  useEffect(() => {
    if (open) {
      loadWhitelist()
    }
  }, [open])

  const loadWhitelist = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getSupplierWhitelist()
      setSuppliers(data.suppliers)
    } catch (err) {
      console.error('Failed to load supplier whitelist:', err)
      setError('Failed to load whitelist')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = async () => {
    if (!newSupplier.id || !newSupplier.normalizedName || !newSupplier.ustId) {
      setError('Please fill in all required fields')
      return
    }

    const names = newSupplier.names[0]
      ? newSupplier.names[0].split(',').map(s => s.trim()).filter(Boolean)
      : [newSupplier.normalizedName]

    const supplierToAdd: WhitelistEntry = {
      ...newSupplier,
      names
    }

    try {
      await addSupplierToWhitelist(supplierToAdd)
      await loadWhitelist()
      setShowAdd(false)
      setNewSupplier({ id: '', names: [''], normalizedName: '', ustId: '' })
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      console.error('Failed to add supplier:', err)
      setError(err instanceof Error ? err.message : 'Failed to add supplier')
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteSupplierFromWhitelist(id)
      await loadWhitelist()
    } catch (err) {
      console.error('Failed to delete supplier:', err)
      setError('Failed to delete supplier')
    }
  }

  const generateId = (name: string) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="subtitle2">Supplier Whitelist</Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setShowAdd(!showAdd)}
        >
          Add Supplier
        </Button>
      </Stack>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Supplier added successfully
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {showAdd && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
            Add new supplier to whitelist
          </Typography>
          <Stack spacing={1}>
            <TextField
              size="small"
              label="ID"
              value={newSupplier.id}
              onChange={(e) => setNewSupplier({ ...newSupplier, id: e.target.value })}
              placeholder="e.g., acme-corp"
              onBlur={() => {
                if (!newSupplier.id && newSupplier.normalizedName) {
                  setNewSupplier({ ...newSupplier, id: generateId(newSupplier.normalizedName) })
                }
              }}
            />
            <TextField
              size="small"
              label="Normalized Name"
              value={newSupplier.normalizedName}
              onChange={(e) => setNewSupplier({ ...newSupplier, normalizedName: e.target.value })}
              placeholder="e.g., ACME Corporation"
            />
            <TextField
              size="small"
              label="USt-ID"
              value={newSupplier.ustId}
              onChange={(e) => setNewSupplier({ ...newSupplier, ustId: e.target.value })}
              placeholder="e.g., DE123456789"
            />
            <TextField
              size="small"
              label="Name Variations (comma-separated)"
              value={newSupplier.names[0] || ''}
              onChange={(e) => setNewSupplier({
                ...newSupplier,
                names: [e.target.value]
              })}
              placeholder="e.g., ACME, Acme Corp, ACME GmbH"
              helperText="Different ways this supplier might appear on invoices"
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                onClick={handleAdd}
                disabled={!newSupplier.id || !newSupplier.normalizedName || !newSupplier.ustId}
              >
                Add to Whitelist
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setShowAdd(false)
                  setNewSupplier({ id: '', names: [''], normalizedName: '', ustId: '' })
                }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {loading ? (
        <Typography variant="body2" color="textSecondary">
          Loading whitelist...
        </Typography>
      ) : suppliers.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No suppliers in whitelist. Add your first supplier above.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {suppliers.map((supplier) => (
            <Paper key={supplier.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {supplier.normalizedName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    ID: {supplier.id} • USt-ID: {supplier.ustId || 'N/A'}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="textSecondary" sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block'
                    }}>
                      Variations: {supplier.names.join(', ')}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(supplier.id)}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
        Suppliers in whitelist: {suppliers.length}
      </Typography>
    </Box>
  )
}
